import { createApiRef, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api'

const PROXY_PATH = '/codecov/api/v2/github'

export interface CodeCoverageTotals {
  coverage: number
  lines: number
  hits: number
  misses: number
  branches?: number
}

export interface CodeCoverageReport {
  owner: string
  repo: string
  updatedAt: string
  totals: CodeCoverageTotals
}

export interface CodeCoverageQuery {
  owner: string
  repo: string
}

export interface CodeCoverageApi {
  getCoverage(query: CodeCoverageQuery): Promise<CodeCoverageReport>
}

export const codeCoverageApiRef = createApiRef<CodeCoverageApi>({
  id: 'plugin.code-coverage.service',
})

export class CodecovRepoNotFoundError extends Error {
  constructor(
    public readonly owner: string,
    public readonly repo: string
  ) {
    super(`Codecov has no record of '${owner}/${repo}'`)
  }
}

interface CodecovV2Response {
  name?: string
  branch?: string
  totals?: {
    coverage?: number
    lines?: number
    hits?: number
    misses?: number
    branches?: number
  } | null
  updatedAt?: string
  updated_at?: string
}

interface Options {
  discoveryApi: DiscoveryApi
  fetchApi: FetchApi
}

export class CodecovCodeCoverageClient implements CodeCoverageApi {
  private readonly discoveryApi: DiscoveryApi
  private readonly fetchApi: FetchApi

  constructor({ discoveryApi, fetchApi }: Options) {
    this.discoveryApi = discoveryApi
    this.fetchApi = fetchApi
  }

  async getCoverage({ owner, repo }: CodeCoverageQuery): Promise<CodeCoverageReport> {
    const proxyBase = await this.discoveryApi.getBaseUrl('proxy')
    const path = `${PROXY_PATH}/${owner}/repos/${repo}`

    const response = await this.fetchApi.fetch(`${proxyBase}${path}`)
    if (response.status === 404) {
      throw new CodecovRepoNotFoundError(owner, repo)
    }
    if (!response.ok) {
      const text = await response.text().catch(() => '')
      throw new Error(`Codecov request failed (${response.status} ${response.statusText}): ${text}`.trim())
    }

    const data = (await response.json()) as CodecovV2Response
    const totals = data.totals
    if (!totals || typeof totals.coverage !== 'number') {
      throw new Error('Codecov response missing coverage totals')
    }

    return {
      owner,
      repo,
      updatedAt: data.updatedAt ?? data.updated_at ?? '',
      totals: {
        coverage: totals.coverage,
        lines: totals.lines ?? 0,
        hits: totals.hits ?? 0,
        misses: totals.misses ?? 0,
        branches: totals.branches,
      },
    }
  }
}
